// ./src/components/features/CreateEventWizard/steps/GuestsStep.tsx
//
// Step 4 — "How will people join?". The pivotal step. First the event MODE
// (open link / invite list / save-the-date / sell tickets) via the shared
// GuestModeField. Then, for open RSVP / invites, the names-vs-headcount choice
// (headcount is the dead-simple path). RSVP options (plus-ones, approval,
// capacity, waitlist, deadline) live in an optional block so most people can just
// move on. Uses the shared field family so it matches the details editor.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, SimpleGrid, Text, Card, Group, Alert } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useAppEnvironment } from '@/contexts';
import { useAppPlans, useFeatureGate } from '@/hooks';
import { AppIcon, NumberField, SwitchField, DateTimeField, formatMarkedDays, formatPickerMoment } from '@/components/generic';
import { deadlinePickerValue, DEADLINE_END_OF_DAY, getFieldError } from '@/utils';
import { WHOLE_SET } from '@/utils/api';
import { joinPlanNames, plansIncludingFeature } from '@/utils/eventFeaturePlans';
import { draftDeadlineCeiling } from '../draftToEventInput';
import { GuestModeField } from '@/components/features/Events';

import { useEventWizard } from '../WizardContext';
import { WizardSelectCard } from '../components/WizardSelectCard';
import { DEFAULT_TICKETS_PER_ORDER, EVENT_MODE, GUEST_TRACKING, GuestTracking, EventWizardDraft, SCHEDULE_MODE, headcountPartyCap } from '../wizardTypes';

interface TrackingOption {
  value: GuestTracking;
  icon: string;
  title: string;
  description: string;
}

const TRACKING_OPTIONS: TrackingOption[] = [
  // ⚠ Both take the main guest's name — headcount counts the party behind them (owner, 2026-10-22).
  { value: GUEST_TRACKING.HEADCOUNT, icon: 'Hash', title: 'Headcount', description: 'The main guest’s name and how many are coming; questions answered once per party' },
  { value: GUEST_TRACKING.NAMES, icon: 'Users', title: 'Every guest by name', description: 'The main guest names everyone; each person can answer their own questions' },
];

export const GuestsStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { isMobile } = useAppEnvironment();
  const { draft, patchDraft, saveError, createdEventId } = useEventWizard();

  // "Every guest by name" is the plan's `guest_list` feature. ROADMAP #231 (owner, 2026-10-22: "let user use any gated
  // feature on draft event and just do not let publish it until they upgrade") — the event is created as a DRAFT, which
  // may use it on any plan (the server's rule 6), so the card stays choosable; on a plan without it, it is MARKED premium
  // with the plans that include it, and publishing asks for one. (Until #231 it was locked, and a draft holding it was
  // moved to headcount — that interim lock is gone.)
  const { checkFeature } = useFeatureGate();
  const namesPremium = !!checkFeature(FEATURE_CODES.GUEST_LIST, { eventId: createdEventId }).premium;
  const { appPlans, list: listPlans } = useAppPlans({ autoLoad: false, pageSize: WHOLE_SET });

  useEffect(() => {
    if (namesPremium) {
      listPlans({}, { page: 1, pageSize: WHOLE_SET }, undefined, true);
    }
  }, [namesPremium, listPlans]);

  const namesPlans = namesPremium ? plansIncludingFeature(appPlans, FEATURE_CODES.GUEST_LIST) : [];

  // Default to Open RSVP so the segmented control always reflects a real choice
  // (and Next isn't blocked on an invisible "nothing selected" state).
  useEffect(() => {
    if (!draft.eventMode) patchDraft({ eventMode: EVENT_MODE.LINK_RSVP });
  }, [draft.eventMode, patchDraft]);

  const mode = draft.eventMode;
  const isTicketed = mode === EVENT_MODE.TICKETED;

  // A2e #94, #137 — the deadline is a date AND time that cannot outlive the event: capped at its END to the minute (its
  // start when it has none; an all-day event's last day), on the event's own clock (Basics' pickers are wall clocks in
  // the event's zone) — the server refuses anything later. Its day(s) are marked, and the sentence says where the cap
  // is. ⓘ A2e #102 — a repeating event has no calendar deadline: it takes "N days before each date" instead.
  const repeats = draft.scheduleMode === SCHEDULE_MODE.RECURRING;
  const eventDays =
    draft.scheduleMode !== SCHEDULE_MODE.RECURRING && draft.startsAt
      ? { from: draft.startsAt.slice(0, 10), to: (draft.endsAt || draft.startsAt).slice(0, 10) }
      : null;
  const deadlineMax = draftDeadlineCeiling(draft);
  const deadlineHint = !eventDays
    ? undefined
    : draft.isAllDay
      ? t('events:wizard.guests.deadlineEventDays', { defaultValue: 'The event is on {{days}}.', days: formatMarkedDays(eventDays) })
      : draft.endsAt
        ? t('events:wizard.guests.deadlineEventEnds', { defaultValue: 'The event ends {{when}}.', when: formatPickerMoment(deadlineMax) })
        : t('events:wizard.guests.deadlineEventStarts', { defaultValue: 'The event starts {{when}}.', when: formatPickerMoment(deadlineMax) });

  // Which follow-ups apply:
  //  - names-vs-headcount for open RSVP, invites AND tickets (sometimes you just
  //    want who bought + how many, not every attendee's name).
  //  - options only when we actually collect RSVPs/tickets (not save-the-date).
  //  - group-size is "plus-ones" for RSVP; a ticketed event has its own "tickets per order" limit (#77);
  //    approval only makes sense when collecting RSVP names (not for ticket buyers).
  //  - ⚠ #139: group-size is asked on a HEADCOUNT event too — it is how many one reply may count, and the public form
  //    asks "How many in your party?" only when it is above 1. Hidden here, every wizard-made headcount event stayed at
  //    1 and could never be told how many were coming.
  const askTracking = mode === EVENT_MODE.LINK_RSVP || mode === EVENT_MODE.MANAGED_INVITES || mode === EVENT_MODE.TICKETED;
  const collectsNames = askTracking && draft.guestTracking === GUEST_TRACKING.NAMES;
  const isHeadcount = askTracking && !isTicketed && draft.guestTracking === GUEST_TRACKING.HEADCOUNT;
  const showRsvpOptions = !!mode && mode !== EVENT_MODE.SAVE_THE_DATE;
  const showGroupSize = askTracking;
  const showApproval = collectsNames && !isTicketed;

  // Whether each optional numeric field is enabled is LOCAL state, not derived
  // from the value — otherwise clearing the number (to retype it) drops the value
  // to null, flips the toggle off and unmounts the input mid-edit. Seed from the
  // draft, and only ever turn ON in response to an external value (restore / step
  // revisit); turning OFF is explicit via the toggle.
  const [allowPlusOnes, setAllowPlusOnes] = useState<boolean>(draft.rsvpGroupSize > 1);
  const [limitPerOrder, setLimitPerOrder] = useState<boolean>(draft.maxTicketsPerOrder != null);
  const [limitCapacity, setLimitCapacity] = useState<boolean>(draft.capacity != null);

  useEffect(() => {
    if (draft.rsvpGroupSize > 1) setAllowPlusOnes(true);
  }, [draft.rsvpGroupSize]);
  useEffect(() => {
    if (draft.maxTicketsPerOrder != null) {
      setLimitPerOrder(true);
    }
  }, [draft.maxTicketsPerOrder]);
  useEffect(() => {
    if (draft.capacity != null) setLimitCapacity(true);
  }, [draft.capacity]);

  // Stable handler for the plain option fields.
  const handleChange = useCallback(
    (name: string | undefined, value: unknown) => {
      if (name) patchDraft({ [name]: value } as Partial<EventWizardDraft>);
    },
    [patchDraft],
  );

  // #139 — the party size "Headcount" raised (a headcount reply counts a party), remembered so that "Every guest by
  // name" straight after puts it back — never over a number the host has typed since.
  const headcountBumpRef = useRef<{ from: number; to: number } | null>(null);

  const chooseTracking = (tracking: GuestTracking) => {
    const bump = headcountBumpRef.current;

    if (tracking === GUEST_TRACKING.HEADCOUNT && !isTicketed) {
      const to = headcountPartyCap(draft.rsvpGroupSize);

      headcountBumpRef.current = to !== draft.rsvpGroupSize ? { from: draft.rsvpGroupSize, to } : null;
      patchDraft({ guestTracking: tracking, rsvpGroupSize: to });

      return;
    }

    headcountBumpRef.current = null;

    if (bump && draft.rsvpGroupSize === bump.to) {
      setAllowPlusOnes(bump.from > 1);
      patchDraft({ guestTracking: tracking, rsvpGroupSize: bump.from });

      return;
    }

    patchDraft({ guestTracking: tracking });
  };

  // Toggles that map a boolean onto a value (kept as their own stable handlers).
  const handlePlusOnesToggle = useCallback(
    (_n: string | undefined, on: boolean) => {
      setAllowPlusOnes(on);
      patchDraft({ rsvpGroupSize: on ? (draft.rsvpGroupSize > 1 ? draft.rsvpGroupSize : 2) : 1 });
    },
    [patchDraft, draft.rsvpGroupSize],
  );
  // #77 — "Limit tickets per order": its own field. OFF is "no limit" (null), never 1 — 1 is a real limit (one ticket
  // per order), so it cannot also mean "off".
  const handleLimitPerOrderToggle = useCallback(
    (_n: string | undefined, on: boolean) => {
      setLimitPerOrder(on);
      patchDraft({ maxTicketsPerOrder: on ? draft.maxTicketsPerOrder ?? DEFAULT_TICKETS_PER_ORDER : null });
    },
    [patchDraft, draft.maxTicketsPerOrder],
  );
  const handleCapacityToggle = useCallback(
    (_n: string | undefined, on: boolean) => {
      setLimitCapacity(on);
      // ⚠ Turning the limit OFF is the host saying "no cap", so the venue's number must not come
      // back: the provenance is cleared with it. Turning it ON by hand makes the number theirs too.
      patchDraft(
        on
          ? { capacity: draft.capacity ?? 50, capacityFromVenueId: null }
          : { capacity: null, capacityFromVenueId: null, enableWaitlist: false },
      );
    },
    [patchDraft, draft.capacity],
  );

  /**
   * ⚠⚠ TYPING A CAPACITY MAKES IT THE HOST'S, and that has to be recorded or the next venue change
   * silently overwrites it. `venueCapacityPatch` only ever replaces a number it put there itself —
   * which is exactly what makes it safe — so the moment a human edits the field, the provenance goes.
   */
  const handleCapacityChange = useCallback(
    (_n: string | undefined, value: number | null) => {
      patchDraft({ capacity: value, capacityFromVenueId: null });
    },
    [patchDraft],
  );

  return (
    <Stack gap="lg">
      {/* Mode — required. GuestModeField renders its own per-mode explanation
          callout (+ optional walkthrough video) beneath the control. No guest
          list yet in the wizard, so no jump-to-guests action here. */}
      <GuestModeField name="eventMode" value={draft.eventMode} onChange={handleChange} />

      {/* Names vs headcount — open RSVP + invites */}
      {askTracking && (
        <Stack gap="xs">
          <Text fw={600} size="md">
            {t('events:wizard.guests.trackingLabel', { defaultValue: 'What do you want to collect?' })}
          </Text>
          <SimpleGrid cols={isMobile ? 1 : 2} spacing="sm">
            {TRACKING_OPTIONS.map((option) => {
              const premium = option.value === GUEST_TRACKING.NAMES && namesPremium;

              return (
                <WizardSelectCard
                  key={option.value}
                  icon={option.icon}
                  title={t(`events:wizard.guests.tracking.${option.value}.title`, { defaultValue: option.title })}
                  description={t(`events:wizard.guests.tracking.${option.value}.desc`, { defaultValue: option.description })}
                  selected={draft.guestTracking === option.value}
                  // #139 — a headcount reply counts a party, so choosing it lets one reply count more than one person.
                  onClick={() => chooseTracking(option.value)}
                  extra={
                    premium ? (
                      <Group gap={6} align="flex-start" wrap="nowrap" mt={4}>
                        <Box pt={3}>
                          <AppIcon iconName="Crown" size={16} color="var(--mantine-color-yellow-6)" />
                        </Box>
                        <Text size="md" c="dimmed">
                          {namesPlans.length
                            ? t('events:wizard.guests.namesPremiumTry', {
                                defaultValue: 'Premium — included in {{plans}}. Use it now; publishing needs one of them.',
                                plans: joinPlanNames(namesPlans),
                              })
                            : t('events:wizard.guests.namesPremiumTryNoPlans', { defaultValue: 'Premium — use it now; publishing needs a plan upgrade.' })}
                        </Text>
                      </Group>
                    ) : null
                  }
                />
              );
            })}
          </SimpleGrid>
        </Stack>
      )}

      {mode === EVENT_MODE.SAVE_THE_DATE && (
        <Alert variant="light" color="brand" icon={<AppIcon iconName="Bell" size={18} />}>
          {t('events:wizard.guests.saveTheDateNote', {
            defaultValue: 'Guests just save the date for now — you can open RSVPs any time after creating the event.',
          })}
        </Alert>
      )}

      {mode === EVENT_MODE.TICKETED && (
        <Alert variant="light" color="grape" icon={<AppIcon iconName="Ticket" size={18} />}>
          {t('events:wizard.guests.ticketedNote', {
            defaultValue: 'You’ll set up ticket types (name, price, quantity) in a later step / after creating the event.',
          })}
        </Alert>
      )}

      {/* RSVP options — optional, tucked into a card so the step stays light */}
      {showRsvpOptions && (
        <Card withBorder radius="md" padding="md">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm">
            {isTicketed
              ? t('events:wizard.guests.ticketOptionsTitle', { defaultValue: 'Ticket options (optional)' })
              : t('events:wizard.guests.optionsTitle', { defaultValue: 'RSVP options (optional)' })}
          </Text>
          <Stack gap="md">
            {showGroupSize && (
              <Stack gap="xs">
                {isTicketed ? (
                  <>
                    <SwitchField
                      label={t('events:wizard.guests.limitOrder', { defaultValue: 'Limit tickets per order' })}
                      description={
                        limitPerOrder
                          ? t('events:wizard.guests.limitOrderHint', {
                              defaultValue: 'Cap how many tickets one order can include (add-ons do not count)',
                            })
                          : t('events:wizard.guests.limitOrderOffHint', {
                              defaultValue: 'Off: one order can include any number of tickets',
                            })
                      }
                      checked={limitPerOrder}
                      onChange={handleLimitPerOrderToggle}
                    />
                    {limitPerOrder && (
                      <NumberField
                        name="maxTicketsPerOrder"
                        label={t('events:wizard.guests.maxPerOrder', { defaultValue: 'Max tickets per order' })}
                        value={draft.maxTicketsPerOrder}
                        onChange={handleChange}
                        min={1}
                        max={1000}
                        w={isMobile ? '100%' : 220}
                        // The server refuses a limit below an admission ticket's smallest order (owner, 2026-10-12).
                        error={getFieldError(saveError, 'maxTicketsPerOrder') || undefined}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <SwitchField
                      label={
                        isHeadcount
                          ? t('events:wizard.guests.partyReplies', { defaultValue: 'One reply can count several people' })
                          : t('events:wizard.guests.plusOnes', { defaultValue: 'Allow plus-ones' })
                      }
                      description={
                        isHeadcount
                          ? t('events:wizard.guests.partyRepliesHint', { defaultValue: 'Guests say how many are coming with them' })
                          : t('events:wizard.guests.plusOnesHint', { defaultValue: 'Let a guest bring others on one RSVP' })
                      }
                      checked={allowPlusOnes}
                      onChange={handlePlusOnesToggle}
                    />
                    {allowPlusOnes && (
                      <NumberField
                        name="rsvpGroupSize"
                        label={
                          isHeadcount
                            ? t('events:wizard.guests.maxPerReply', { defaultValue: 'Max people per reply' })
                            : t('events:wizard.guests.maxPerRsvp', { defaultValue: 'Max people per RSVP' })
                        }
                        value={draft.rsvpGroupSize}
                        onChange={handleChange}
                        min={2}
                        max={50}
                        w={isMobile ? '100%' : 220}
                      />
                    )}
                  </>
                )}
              </Stack>
            )}

            {showApproval && (
              <SwitchField
                name="guestsNeedApproval"
                label={t('events:wizard.guests.approval', { defaultValue: 'Approve guests before they’re confirmed' })}
                checked={draft.guestsNeedApproval}
                onChange={handleChange}
              />
            )}

            <Stack gap="xs">
              <SwitchField
                label={t('events:wizard.guests.limitCapacity', { defaultValue: 'Limit the number of guests' })}
                checked={limitCapacity}
                onChange={handleCapacityToggle}
              />
              {limitCapacity && (
                <Stack gap="xs">
                  <NumberField
                    name="capacity"
                    label={t('events:wizard.guests.capacity', { defaultValue: 'Capacity' })}
                    value={draft.capacity}
                    onChange={handleCapacityChange}
                    min={1}
                    w={isMobile ? '100%' : 180}
                  />
                  <Text size="xs" c="dimmed">
                    {t('events:wizard.guests.capacityPlanNote', {
                      defaultValue:
                        'Your plan sets how many guests you can host — larger events may need an upgrade.',
                    })}
                  </Text>
                  {/* ⓘ No plan badge: waitlists are on every plan since 2026-10-22 (owner). */}
                  <SwitchField
                    name="enableWaitlist"
                    label={t('events:wizard.guests.waitlist', { defaultValue: 'Enable waitlist when full' })}
                    checked={draft.enableWaitlist}
                    onChange={handleChange}
                  />
                </Stack>
              )}
            </Stack>

            {repeats ? (
              // A2e #102 — a calendar day can fit only one date of a series: each date closes this many days before it.
              <NumberField
                name="rsvpDeadlineDaysBefore"
                label={
                  isTicketed
                    ? t('events:wizard.guests.ticketDeadlineSeries', { defaultValue: 'Stop selling tickets (optional)' })
                    : t('events:wizard.guests.rsvpDeadlineSeries', { defaultValue: 'Stop taking RSVPs (optional)' })
                }
                description={t('events:wizard.guests.deadlineDaysBeforeHint', {
                  defaultValue: 'Days before each date. 0 = on the day itself. Leave empty for none — a date’s own deadline still applies.',
                })}
                placeholder={t('events:wizard.guests.rsvpDeadlinePlaceholder', { defaultValue: 'No deadline' })}
                value={draft.rsvpDeadlineDaysBefore}
                onChange={handleChange}
                min={0}
                max={365}
                allowDecimal={false}
                suffix={
                  draft.rsvpDeadlineDaysBefore === 1
                    ? t('events:wizard.guests.dayBeforeSuffix', { defaultValue: ' day before' })
                    : t('events:wizard.guests.daysBeforeSuffix', { defaultValue: ' days before' })
                }
                error={getFieldError(saveError, 'rsvpDeadlineDaysBefore') || undefined}
                w={isMobile ? '100%' : 320}
              />
            ) : (
              <DateTimeField
                name="rsvpDeadline"
                label={
                  isTicketed
                    ? t('events:wizard.guests.ticketDeadline', { defaultValue: 'Ticket sales end (optional)' })
                    : t('events:wizard.guests.rsvpDeadline', { defaultValue: 'RSVPs close (optional)' })
                }
                placeholder={t('events:wizard.guests.rsvpDeadlinePlaceholder', { defaultValue: 'No deadline' })}
                // ⚠ A draft saved before #137 may hold a DAY: read as that day's last second, never its first.
                value={deadlinePickerValue(draft.rsvpDeadline, deadlineMax)}
                onChange={handleChange}
                notAfter={deadlineMax}
                notAfterErrorText={
                  draft.isAllDay
                    ? t('events:wizard.guests.deadlineAfterEventDay', { defaultValue: 'Can’t be after the event’s last day' })
                    : draft.endsAt
                      ? t('events:wizard.guests.deadlineAfterEvent', { defaultValue: 'Can’t be after the event ends' })
                      : t('events:wizard.guests.deadlineAfterEventStart', { defaultValue: 'Can’t be after the event starts' })
                }
                defaultTime={DEADLINE_END_OF_DAY}
                markDays={eventDays}
                description={deadlineHint}
                error={getFieldError(saveError, 'rsvpDeadline') || undefined}
                leftSection={<AppIcon iconName="CalendarTime" size={16} />}
                clearable
                w={isMobile ? '100%' : 320}
              />
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
};
